import styled from "styled-components";
import { Collapse } from "react-bootstrap";
import { useState, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { useForm, FormProvider } from "react-hook-form";
import { faPhone } from "@fortawesome/free-solid-svg-icons";
import {
  Wrapper,
  ProgressBar,
  ProgressFill,
  ProgressWrap,
  OtpFlowTitle,
  ProgressLabel,
  OtpFlowSubText
} from "../../common-styles";
import {
  OTP_LOADING_KEYS,
  MAX_NUMBER_CORRECTIONS
} from "../../templates/strings";
import {
  selectPhone,
  selectLoading,
  clearOtpError,
  selectOtpError,
  selectSessionExpired,
  selectResendAvailableAt,
  selectNumberCorrectCount
} from "../../store/slices/otpSlice";
import PhoneInputField from "../PhoneInput";
import { getPhoneCountryCodes } from "../../services/utils";
import {
  resendOtpThunk,
  manualFallbackThunk
} from "../../store/thunks/otpThunks";
import { updateUrlParamsPhone } from "../../store/slices/funnelInfo";
import { PhoneNumberSchema } from "../../services/validation-schema";

const PrimaryBtn = styled.button`
  width: 100%;
  padding: 15px 0;
  background: ${({ disabled }) => (disabled ? "#aaa" : "#1a1a1a")};
  border: none;
  border-radius: 6px;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  margin-top: 8px;
  margin-bottom: 12px;
  transition: background 0.2s;
  @media (max-width: 481px) {
    font-size: 16px;
    padding: 12px 0;
  }
`;
// $type: "error" | "info"
const InfoBanner = styled.div`
  padding: 12px 14px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 16px;
  background: ${({ $type }) => ($type === "error" ? "#fef2f2" : "#eff6ff")};
  border: 1px solid
    ${({ $type }) => ($type === "error" ? "#fca5a5" : "#bfdbfe")};
  color: ${({ $type }) => ($type === "error" ? "#b91c1c" : "#1d4ed8")};
  @media (max-width: 481px) {
    font-size: 10px;
  }
`;
const SecondaryBtn = styled.button`
  width: 100%;
  padding: 15px 0;
  background: #fff;
  border: 2px solid;
  border-radius: 6px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: #f5f5f5;
  }
  @media (max-width: 481px) {
    font-size: 16px;
    padding: 12px 0;
  }
`;
const PhoneInputWrap = styled.div`
  margin: 15px 15px 0;
`;
const UpdateResendBtn = styled.button`
  width: 100%;
  padding: 14px 0;
  background: ${({ disabled }) => (disabled ? "#ccc" : "#e94e1b")};
  border: none;
  border-radius: 6px;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  margin-top: 4px;
  transition: background 0.2s;
  @media (max-width: 481px) {
    font-size: 12px;
  }
`;
const Divider = styled.hr`
  border: none;
  border-top: 1px solid #a7a7a7;
  margin: 40px 0;
  @media (max-width: 481px) {
    margin: 25px 0;
  }
`;
const FallbackLabel = styled.p`
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 15px;
`;
const FallbackLink = styled.span`
  display: block;
  text-align: center;
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    color: #555;
  }
`;
const ExpiredBox = styled.div`
  text-align: center;
  padding: 20px 0;
`;
const ExpiredTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 10px;
`;
const ExpiredText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 20px;
`;
const WarningBanner = styled.div`
  font-size: 12px;
  padding: 5px;
  margin-top: 5px;
  border-radius: 6px;
  background: #fff3cd;
  border: 1px solid #ffeeba;
  color: #856404;
`;

const EscalationScreen = ({ leadId }) => {
  const dispatch = useDispatch();
  const countryOption = useSelector(
    (state) => state.countryOption.countryOption
  );
  const currentPhone = useSelector(selectPhone);
  const isResending = useSelector(selectLoading(OTP_LOADING_KEYS.RESEND_OTP));
  const isFallback = useSelector(
    selectLoading(OTP_LOADING_KEYS.MANUAL_FALLBACK)
  );
  const resendAvailableAt = useSelector(selectResendAvailableAt);
  const sessionExpired = useSelector(selectSessionExpired);
  const otpError = useSelector(selectOtpError);
  const numberCorrectCount = useSelector(selectNumberCorrectCount);

  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const meetsMinimumNumberCorrectCount =
    numberCorrectCount >= MAX_NUMBER_CORRECTIONS;

  // ── Resend cooldown countdown
  useEffect(() => {
    if (!resendAvailableAt) {
      setCanResend(true);
      return;
    }
    const tick = () => {
      const ms = new Date(resendAvailableAt).getTime() - Date.now();
      if (ms <= 0) {
        setSecondsLeft(0);
        setCanResend(true);
      } else {
        setSecondsLeft(Math.ceil(ms / 1000));
        setCanResend(false);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [resendAvailableAt]);

  // Clear error when user interacts
  const clearError = () => {
    if (otpError) dispatch(clearOtpError());
  };

  const methods = useForm({
    defaultValues: { phone: currentPhone || "" },
    resolver: yupResolver(PhoneNumberSchema)
  });
  const {
    formState: { errors },
    handleSubmit,
    watch
  } = methods;
  const watchedPhone = watch("phone");

  // ── Button 1: Resend immediately
  const handleResend = () => {
    if (!canResend || isResending || sessionExpired) return;
    clearError();
    dispatch(resendOtpThunk({ leadId, newPhone: null }));
  };

  // ── Button 2: Toggle phone input
  const handleTogglePhoneInput = () => {
    clearError();
    setShowPhoneInput((prev) => !prev);
  };

  // ── Phone input: update number + resend
  const handleUpdateAndResend = handleSubmit((data) => {
    if (!data.phone || isResending || sessionExpired) return;
    clearError();
    dispatch(resendOtpThunk({ leadId, newPhone: data.phone }));
    dispatch(updateUrlParamsPhone({ newPhone: data.phone }));
  });

  // ── Fallback link
  const handleManualFallback = () => {
    dispatch(manualFallbackThunk({ leadId }));
  };

  // ── Session expired state — show dedicated message
  // markSessionExpired() sets screen to "thanks-manual" which unmounts this
  // component via OtpFlow. But as a safety net, handle it here too.
  if (sessionExpired) {
    return (
      <Wrapper>
        <ExpiredBox>
          <ExpiredTitle>Sitzung abgelaufen</ExpiredTitle>
          <ExpiredText>
            Ihre 10-minütige Verifizierungszeit ist abgelaufen. Sie werden nun
            telefonisch kontaktiert.
          </ExpiredText>
          <FallbackLink onClick={handleManualFallback} style={{ fontSize: 14 }}>
            {isFallback ? "Wird weitergeleitet..." : "Jetzt weiterleiten"}
          </FallbackLink>
        </ExpiredBox>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <ProgressWrap>
        <ProgressBar>
          <ProgressFill $width="85" />
        </ProgressBar>
        <ProgressLabel>Fast geschafft</ProgressLabel>
      </ProgressWrap>

      <OtpFlowTitle>Keinen Code erhalten?</OtpFlowTitle>
      <OtpFlowSubText>
        Das passiert manchmal. So kommst du weiter:
      </OtpFlowSubText>

      {/* ── Error / info banner ─────────────────────────────────────────── */}
      {otpError && <InfoBanner $type="error">{otpError}</InfoBanner>}

      {/* ── Cooldown active info ─────────────────────────────────────────── */}
      {!canResend && !otpError && (
        <InfoBanner $type="info">
          Bitte warte noch <strong>{secondsLeft} Sekunden</strong>, bevor du den
          Code erneut anforderst.
        </InfoBanner>
      )}

      {/* Button 1 — resend immediately */}
      <PrimaryBtn
        onClick={handleResend}
        disabled={!canResend || isResending || sessionExpired}
      >
        {isResending
          ? "Wird gesendet..."
          : !canResend
            ? `Erneut senden in ${secondsLeft}s`
            : "Code erneut senden"}
      </PrimaryBtn>

      {/* Button 2 — toggle phone input */}
      <SecondaryBtn
        onClick={handleTogglePhoneInput}
        disabled={meetsMinimumNumberCorrectCount}
      >
        {showPhoneInput ? "Abbrechen" : "Nummer korrigieren"}
      </SecondaryBtn>
      {/* Warning banner */}
      {meetsMinimumNumberCorrectCount && (
        <WarningBanner>
          Dieser Vorgang ist aktuell nicht mehr möglich
        </WarningBanner>
      )}

      {/* Inline phone input */}
      <Collapse in={showPhoneInput && !meetsMinimumNumberCorrectCount}>
        <div>
          <PhoneInputWrap>
            <FormProvider {...methods}>
              <PhoneInputField
                field={{
                  countries: getPhoneCountryCodes(countryOption),
                  icon: faPhone,
                  name: "phone",
                  title: "Telefonnummer",
                  hasError: errors?.phone,
                  placeholder: "z.B. +49 170 1234567"
                }}
              />
            </FormProvider>
            <UpdateResendBtn
              onClick={handleUpdateAndResend}
              disabled={isResending || !watchedPhone || sessionExpired}
            >
              {isResending
                ? "Wird gesendet..."
                : "Nummer aktualisieren & Code senden"}
            </UpdateResendBtn>
          </PhoneInputWrap>
        </div>
      </Collapse>

      {/* Divider + fallback */}
      <Divider />
      <FallbackLabel>Geht trotzdem nichts?</FallbackLabel>
      <FallbackLink onClick={handleManualFallback}>
        {isFallback
          ? "Wird weitergeleitet..."
          : "Lass dich telefonisch zurückrufen"}
      </FallbackLink>
    </Wrapper>
  );
};

export default EscalationScreen;
